/**
 * Latency monitoring — times "play() call → first audible event".
 *
 * Pure logic. The TonePlayer captures `t0 = performance.now()` at .play()
 * entry and feeds the first scheduled note's wall-clock fire time into the
 * monitor. The monitor subtracts the note's intended offset to isolate JS +
 * scheduling overhead, warns above the budget, and forwards the value to an
 * optional consumer (tests, telemetry).
 *
 * Spec: latency budget is 200ms (CLAUDE.md week 2).
 */

export const DEFAULT_LATENCY_BUDGET_MS = 200;

export interface LatencyMonitorOptions {
  /** Warning threshold in ms. Defaults to 200. */
  budgetMs?: number;
  /** Hook for tests / telemetry. Receives every measurement. */
  onMeasure?: (latencyMs: number, ctx: LatencyMeasurement) => void;
  /**
   * Override the default `console.warn` sink. Useful in tests where we want
   * to capture warnings without mutating console.
   */
  warn?: (message: string) => void;
}

export interface LatencyMeasurement {
  /** Time from play() invocation to first note becoming audible, in ms. */
  latencyMs: number;
  /** The 200ms (default) budget that was applied. */
  budgetMs: number;
  /** Whether the measurement exceeded the budget. */
  exceededBudget: boolean;
}

/**
 * Compute latency from a play() invocation timestamp and the moment the
 * first audible event actually fired. Subtracts the note's intended offset
 * within the plan so we measure only scheduling/processing overhead.
 *
 *   latency = (firstAudibleAtMs - playInvokedAtMs) - firstEventOffsetSec * 1000
 */
export function measureLatency(
  playInvokedAtMs: number,
  firstAudibleAtMs: number,
  firstEventOffsetSec: number,
  budgetMs = DEFAULT_LATENCY_BUDGET_MS,
): LatencyMeasurement {
  const rawMs = firstAudibleAtMs - playInvokedAtMs - firstEventOffsetSec * 1000;
  const latencyMs = Math.max(0, rawMs);
  return {
    latencyMs,
    budgetMs,
    exceededBudget: latencyMs > budgetMs,
  };
}

export class LatencyMonitor {
  private readonly budgetMs: number;
  private readonly onMeasure?: (ms: number, ctx: LatencyMeasurement) => void;
  private readonly warn: (message: string) => void;
  private playInvokedAtMs: number | null = null;
  private firstEventOffsetSec = 0;
  private armed = false;

  constructor(opts: LatencyMonitorOptions = {}) {
    this.budgetMs = opts.budgetMs ?? DEFAULT_LATENCY_BUDGET_MS;
    this.onMeasure = opts.onMeasure;
    this.warn =
      opts.warn ??
      ((m) => {
        // eslint-disable-next-line no-console
        console.warn(m);
      });
  }

  /** Call when play() begins. firstEventOffsetSec is the smallest startTime in the plan. */
  armPlay(playInvokedAtMs: number, firstEventOffsetSec: number): void {
    this.playInvokedAtMs = playInvokedAtMs;
    this.firstEventOffsetSec = firstEventOffsetSec;
    this.armed = true;
  }

  /** Call from the first onNoteStart fire after armPlay. No-op if not armed. */
  noteFired(firstAudibleAtMs: number): LatencyMeasurement | null {
    if (!this.armed || this.playInvokedAtMs === null) return null;
    this.armed = false;
    const m = measureLatency(
      this.playInvokedAtMs,
      firstAudibleAtMs,
      this.firstEventOffsetSec,
      this.budgetMs,
    );
    if (m.exceededBudget) {
      this.warn(
        `[audio-engine-web] latency ${m.latencyMs.toFixed(1)}ms exceeds ${m.budgetMs}ms budget`,
      );
    }
    this.onMeasure?.(m.latencyMs, m);
    return m;
  }

  /** Cancel an armed measurement (e.g. play() was aborted before first note). */
  reset(): void {
    this.playInvokedAtMs = null;
    this.firstEventOffsetSec = 0;
    this.armed = false;
  }
}
